import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import { IDataTableV2DateState } from "../../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { IJeType, IJeEntry } from "./JeTypeAndEntry.types";
import { uploadFile } from "../../../features/FileUpload/utils";

export const FETCH_JE_TYPE_FAILURE = "FETCH_JE_TYPE_FAILURE";
export const FETCH_JE_TYPE_PROGRESS = "FETCH_JE_TYPE_PROGRESS";
export const FETCH_JE_TYPE_SUCCESS = "FETCH_JE_TYPE_SUCCESS";

export const fetchJeTypeProgress = () => action(FETCH_JE_TYPE_PROGRESS);
export const fetchJeTypeSuccess = (data: IJeType) =>
  action(FETCH_JE_TYPE_SUCCESS, { data });
export const fetchJeTypeFailure = (errorMessage: string) =>
  action(FETCH_JE_TYPE_FAILURE, { errorMessage });

export const fetchJeTypeAsync =
  (je_type_number: number): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchJeTypeProgress());

      const res = await api.get(
        `/accounting/get-je-type?je_type_number=${je_type_number}`
      );
      const data: IJeType[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchJeTypeSuccess(data[0]));
      }
    } catch (err: any) {
      dispatch(fetchJeTypeFailure("Something went to be wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const upsertJeTypeAsync =
  (
    data: IJeType,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      await api.post("/accounting/upsert-je-type", {
        ...data,
      });
      const message = "Je Type saved successfully.";
      dispatch(
        showMessage({
          type: "success",
          message: message,
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_JE_TYPE_LIST_PROGRESS = "FETCH_JE_TYPE_LIST_PROGRESS";
export const FETCH_JE_TYPE_LIST_SUCCESS = "FETCH_JE_TYPE_LIST_SUCCESS";
export const FETCH_JE_TYPE_LIST_FAILURE = "FETCH_JE_TYPE_LIST_FAILURE";

export const fetchJeTypeListProgress = () =>
  action(FETCH_JE_TYPE_LIST_PROGRESS);
export const fetchJeTypeListSuccess = (data: IJeType[]) =>
  action(FETCH_JE_TYPE_LIST_SUCCESS, { data });
export const fetchJeTypeListFailure = (errorMessage: string) =>
  action(FETCH_JE_TYPE_LIST_FAILURE, { errorMessage });

export const fetchJeTypeListAsync = (
  pageNumber: number,
  rowsInPerPage: number,
  limit: number,
  status: string,
  date: IDataTableV2DateState["dates"],
  searchValue: IDatatableV2AdvancedSearchFilter
): ThunkAction<void, IStoreState, {}, AnyAction> => {
  return async (dispatch, getState) => {
    try {
      dispatch(fetchJeTypeListProgress());

      let finalUrl = `/accounting/get-je-type?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (searchValue.length > 0) {
        finalUrl = `/accounting/get-je-type?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${
          date.fromDate
        }&to_date=${date.toDate}&advanceFilter=${JSON.stringify(searchValue)}`;
      }
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }

      const res = await api.get(finalUrl);
      const data: IJeType[] = res.data.data;
      dispatch(fetchJeTypeListSuccess(data));
    } catch (err: any) {
      dispatch(fetchJeTypeListFailure("Something went to be wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };
};

export const fetchAllJeTypeListAsync = (): ThunkAction<
  void,
  IStoreState,
  {},
  AnyAction
> => {
  return async (dispatch, getState) => {
    try {
      dispatch(fetchJeTypeListProgress());
      const finalUrl = `/accounting/get-je-type?status=ACTIVE`;

      const res = await api.get(finalUrl);
      const data: IJeType[] = res.data.data;
      dispatch(fetchJeTypeListSuccess(data));
    } catch (err: any) {
      dispatch(fetchJeTypeListFailure("Something went to be wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };
};

export const CLEAR_JE_TYPE = "CLEAR_JE_TYPE";
export const clearJeType = () => action(CLEAR_JE_TYPE);

// JE Entry Actions
export const FETCH_JE_ENTRY_FAILURE = "FETCH_JE_ENTRY_FAILURE";
export const FETCH_JE_ENTRY_PROGRESS = "FETCH_JE_ENTRY_PROGRESS";
export const FETCH_JE_ENTRY_SUCCESS = "FETCH_JE_ENTRY_SUCCESS";

export const fetchJeEntryProgress = () => action(FETCH_JE_ENTRY_PROGRESS);
export const fetchJeEntrySuccess = (data: IJeEntry) =>
  action(FETCH_JE_ENTRY_SUCCESS, { data });
export const fetchJeEntryFailure = (errorMessage: string) =>
  action(FETCH_JE_ENTRY_FAILURE, { errorMessage });

export const fetchJeEntryAsync =
  (unique_je_code: number): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchJeEntryProgress());

      const res = await api.get(
        `/accounting/get-je-entry?unique_je_code=${unique_je_code}`
      );
      const data: IJeEntry[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchJeEntrySuccess(data[0]));
      }
    } catch (err: any) {
      dispatch(fetchJeEntryFailure("Something went wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const UPSERT_JE_ENTRY_SUCCESS = "UPSERT_JE_ENTRY_SUCCESS";

export const upsertJeEntrySuccess = (data: IJeEntry) =>
  action(UPSERT_JE_ENTRY_SUCCESS, data);

export const upsertJeEntryAsync =
  (
    data: {[key: string]: any},
    onCallback: (isSuccess: boolean, data?: IJeEntry) => void,
    isDelete?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
       dispatch(saveLoaderProgress());
      const res = await api.post("/accounting/upsert-je-entry", {
        ...data,
      });
      let message = "JE Entry saved successfully.";
      if(isDelete){
       message = "JE Entry deleted successfully.";
      }
      dispatch(
        showMessage({
          type: "success",
          message: message,
          displayAs: "snackbar",
        })
      );
      dispatch(upsertJeEntrySuccess(res.data.data));
      onCallback(true, res.data.data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

  export const editJeEntryAsync =
  (
    data: { [key: string]: any },
    isDelete?: boolean
  ): ThunkAction<Promise<IJeEntry>, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      const {insert_ts, create_ts, ...rest} = data;
      const res = await api.post("/accounting/upsert-je-entry", {
        ...rest,
      });


      return res.data.data;
    } catch (err: any) {
     
     
      throw err; 
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };


export const FETCH_JE_ENTRY_LIST_PROGRESS = "FETCH_JE_ENTRY_LIST_PROGRESS";
export const FETCH_JE_ENTRY_LIST_SUCCESS = "FETCH_JE_ENTRY_LIST_SUCCESS";
export const FETCH_JE_ENTRY_LIST_FAILURE = "FETCH_JE_ENTRY_LIST_FAILURE";

export const fetchJeEntryListProgress = () =>
  action(FETCH_JE_ENTRY_LIST_PROGRESS);
export const fetchJeEntryListSuccess = (data: IJeEntry[]) =>
  action(FETCH_JE_ENTRY_LIST_SUCCESS, { data });
export const fetchJeEntryListFailure = (errorMessage: string) =>
  action(FETCH_JE_ENTRY_LIST_FAILURE, { errorMessage });

export const fetchJeEntryListAsync = (
  pageNumber: number,
  rowsInPerPage: number,
  limit: number,
  status: string,
  date: IDataTableV2DateState["dates"],
  searchValue: IDatatableV2AdvancedSearchFilter
): ThunkAction<void, IStoreState, {}, AnyAction> => {
  return async (dispatch, getState) => {
    try {
      dispatch(fetchJeEntryListProgress());

      let finalUrl = `/accounting/get-je-entry?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (searchValue.length > 0) {
        finalUrl = `/accounting/get-je-entry?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${
          date.fromDate
        }&to_date=${date.toDate}&advanceFilter=${JSON.stringify(searchValue)}`;
      }
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }

      const res = await api.get(finalUrl);
      const data: IJeEntry[] = res.data.data;
      dispatch(fetchJeEntryListSuccess(data));
    } catch (err: any) {
      dispatch(fetchJeEntryListFailure("Something went wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };
};

export const fetchAllJeEntryListAsync = (): ThunkAction<
  void,
  IStoreState,
  {},
  AnyAction
> => {
  return async (dispatch, getState) => {
    try {
      dispatch(fetchJeEntryListProgress());
      const finalUrl = `/accounting/get-je-entry?status=ACTIVE`;

      const res = await api.get(finalUrl);
      const data: IJeEntry[] = res.data.data;
      dispatch(fetchJeEntryListSuccess(data));
    } catch (err: any) {
      dispatch(fetchJeEntryListFailure("Something went wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };
};

export const fetchJeEntryByJENumberAsync = (
  je_entry_number: string,
  onCallback: (data: IJeEntry[], error?: string) => void
): ThunkAction<void, IStoreState, {}, AnyAction> => {
  return async (dispatch, getState) => {
    try {
      const finalUrl = `/accounting/get-je-entry?je_entry_number=${je_entry_number}`;
      const res = await api.get(finalUrl);
      const data: IJeEntry[] = res.data.data;
      onCallback(data);
    } catch (err: any) {
      onCallback([]);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };
};


export const CLEAR_JE_ENTRY = "CLEAR_JE_ENTRY";
export const clearJeEntry = () => action(CLEAR_JE_ENTRY);

export const UPDATE_JE_ENTRY_RECONCILIATION_STATUS =
  "UPDATE_JE_ENTRY_RECONCILIATION_STATUS";
export const updateJeEntryReconciliationStatus = (
  uniqueJeCodes: string[],
  reconciled: string,
  reconciledDate: string
) =>
  action(UPDATE_JE_ENTRY_RECONCILIATION_STATUS, {
    uniqueJeCodes,
    reconciled,
    reconciledDate,
  });

export const updateReconciledStatusOfJeEntry =
  (
    data: {
      unique_je_codes: string[];
      reconciled: string;
      reconciled_date: string | null;
    },
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      await api.put("/accounting/update-je-entry-reconciliation-status", {
        type: "je_entries",
        primary_id: data.unique_je_codes,
        reconciled: data.reconciled,
        reconciled_date: data.reconciled_date,
      });
      const message = "JE Entry reconciled successfully.";
      dispatch(
        showMessage({
          type: "success",
          message: message,
          displayAs: "snackbar",
        })
      );
      dispatch(
        updateJeEntryReconciliationStatus(
          data.unique_je_codes,
          data.reconciled,
          data.reconciled_date as string
        )
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const importJeEntryFileAsync =
  (
    file: File,
    status: string,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());

      const asPayload = {
        page_name: "TEST",
      };

      const filePath = await uploadFile(file, "TEST", "", asPayload);

      await api.post("/accounting/extract-csv-data-for-journal-entries", {
        url: filePath,
        status: status,
      });

      dispatch(
        showMessage({
          type: "success",
          message: "File imported successfully.",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message || "Failed to import file.",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
